import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const WS_URL = BACKEND_URL.replace('http', 'ws') + '/api/ws/sheet2';

const Sheet2Tab = () => {
    const [config, setConfig] = useState({
        'Status': 'Stopped',
        'INDEX': {
            'NIFTY': { 'LOT': 0, 'Capital': 0, 'TF': 0, 'CN': 0, 'Ctype': '', 'BUY_SELL': '', 'LEGS': 0, 'Price': 0, 'Imm_Entry': '', 'Update': '' },
            'BANKNIFTY': { 'LOT': 0, 'Capital': 0, 'TF': 0, 'CN': 0, 'Ctype': '', 'BUY_SELL': '', 'LEGS': 0, 'Price': 0, 'Imm_Entry': '', 'Update': '' },
            'SENSEX': { 'LOT': 0, 'Capital': 0, 'TF': 0, 'CN': 0, 'Ctype': '', 'BUY_SELL': '', 'LEGS': 0, 'Price': 0, 'Imm_Entry': '', 'Update': '' }
        },
        'NBS': {
            'ST1': { 'Time': '', 'Index': '', 'SL': '', 'TGT': '', 'Re_Entry': '', 'T_Hit': '', 'SL_Hit': '' },
            'ST2': { 'Time': '', 'Index': '', 'SL': '', 'TGT': '', 'Re_Entry': '', 'T_Hit': '', 'SL_Hit': '' },
            'ST3': { 'Time': '', 'Index': '', 'SL': '', 'TGT': '', 'Re_Entry': '', 'T_Hit': '', 'SL_Hit': '' }
        },
        'TRAILING': '', 'ALL_GREEN_VAL': '',
        'NIFTY_SPOT': 0, 'BANKNIFTY_SPOT': 0, 'SENSEX_SPOT': 0,
        'MAX_PROFIT': 0, 'MAX_LOSS': 0, 'MTM': 0, 'ROI': 0,
        'MAX_CAPITAL': 0, 'GREEN_CAPITAL': 0, 'G_ROI': 0, 'TOTAL_LEGS': 0,
        'TAXES': 0, 'MR+': 0, 'MR-': 0,
        'LT_VT': 'VT', 'LIMIT_MARKET': 'MARKET'
    });

    // Trade Grid
    const [gridData, setGridData] = useState([]);
    const socketRef = useRef(null);

    useEffect(() => {
        const connect = () => {
            const ws = new WebSocket(WS_URL);
            socketRef.current = ws;

            ws.onopen = () => {
                toast.success("Sheet 2 Connected");
                // Request initial state
                ws.send(JSON.stringify({ type: 'get_state' }));
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'initial_state' || data.type === 'update') {
                        if (data.json_disply) {
                            setConfig(prev => ({ ...prev, ...data.json_disply }));
                        }
                        if (data.dfNB) {
                            // Assuming backend sends dfNB as list of dicts
                            setGridData(data.dfNB);
                        }
                    }
                } catch (e) {
                    console.error("Parse Error", e);
                }
            };

            ws.onclose = () => {
                setTimeout(connect, 3000);
            };
        };
        connect();
        return () => socketRef.current?.close();
    }, []);

    const sendAction = async (action) => {
        try {
            await axios.post(`${BACKEND_URL}/api/sheet2/action`, { action }, { withCredentials: true });
            toast.success(`Action ${action} Sent`);
        } catch (e) {
            toast.error(`Failed: ${action}`);
        }
    };

    const handleStart = async () => axios.post(`${BACKEND_URL}/api/sheet2/start`, {}, { withCredentials: true });
    const handleStop = async () => axios.post(`${BACKEND_URL}/api/sheet2/stop`, {}, { withCredentials: true });

    // Sync Config to Backend
    const updateConfig = async (newConfig) => {
        try {
            await axios.post(`${BACKEND_URL}/api/sheet2/config`, newConfig, { withCredentials: true });
            toast.success("Config Synced");
        } catch (e) {
            toast.error("Config Sync Failed");
        }
    };

    const handleInputChange = (section, key, subkey, value) => {
        setConfig(prev => {
            const copy = { ...prev };
            if (section === 'INDEX' || section === 'NBS' || section === 'SELL_CAP') {
                if (!copy[section][key]) copy[section][key] = {};
                copy[section][key][subkey] = value;
            } else {
                copy[key] = value;
            }
            return copy;
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            updateConfig(config);
        }
    };

    // Helper to render Index Row
    const renderIndexRow = (name) => {
        const row = config.INDEX[name] || {};
        return (
            <tr key={name} className="border-b bg-gray-50">
                <td className="p-1 font-bold bg-cyan-100">{name}</td>
                {['LOT', 'Capital', 'TF', 'CN', 'Ctype', 'BUY_SELL', 'LEGS', 'Price', 'Imm_Entry', 'Update'].map(field => (
                    <td key={field} className="p-1 border">
                        <Input
                            className="h-6 text-xs w-full text-center"
                            value={row[field] || ''}
                            onChange={(e) => handleInputChange('INDEX', name, field, e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    </td>
                ))}
            </tr>
        );
    };

    return (
        <div className="p-2 space-y-2 text-xs">
            {/* Top Config Area */}
            <div className="flex gap-2 items-start">
                <div className="bg-white p-2 border shadow rounded">
                    <table className="w-full text-center">
                        <thead>
                            <tr className="bg-blue-600 text-white font-bold">
                                <th>INDEX</th><th>LOT</th><th>Capital</th><th>TF</th><th>CN</th><th>Ctype</th><th>BUY / SELL</th><th>LEGS</th><th>Price</th><th>Immediate Entry</th><th>Update</th>
                            </tr>
                        </thead>
                        <tbody>
                            {['NIFTY', 'BANKNIFTY', 'SENSEX'].map(renderIndexRow)}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col gap-1 w-48">
                    <Button className="bg-green-600 h-8" onClick={handleStart}>START</Button>
                    <Button className="bg-red-600 h-8" onClick={handleStop}>STOP</Button>
                    <div className={`p-2 text-center font-bold text-white rounded ${config.Status === 'Running' ? 'bg-green-500' : 'bg-red-500'}`}>
                        {config.Status}
                    </div>
                    <Button className="bg-blue-500 h-6 text-xs mt-2" onClick={() => updateConfig(config)}>Sync Config</Button>
                </div>
            </div>

            {/* Strategy & Stats Area */}
            <div className="grid grid-cols-12 gap-2">
                <div className="col-span-8 bg-white border shadow p-2">
                    <h3 className="font-bold mb-1">Strategies (NBS)</h3>
                    <table className="w-full text-center border">
                        <thead>
                            <tr className="bg-gray-200">
                                <th>Strategy</th><th>Time</th><th>Index</th><th>SL</th><th>TGT</th><th>Re Entry</th><th>T hit</th><th>SL hit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {['ST1', 'ST2', 'ST3'].map(st => (
                                <tr key={st} className="border-b">
                                    <td className="font-bold">{st}</td>
                                    {['Time', 'Index', 'SL', 'TGT', 'Re_Entry'].map(f => (
                                        <td key={f} className="p-0">
                                            <Input
                                                className="h-6 text-center"
                                                value={config.NBS[st]?.[f] || ''}
                                                onChange={(e) => handleInputChange('NBS', st, f, e.target.value)}
                                                onKeyDown={handleKeyDown}
                                            />
                                        </td>
                                    ))}
                                    <td className="p-0 border">{config.NBS[st]?.T_Hit}</td>
                                    <td className="p-0 border">{config.NBS[st]?.SL_Hit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="col-span-4 bg-white border shadow p-2 grid grid-cols-2 gap-1 text-center">
                    <div className="bg-green-200 p-1 font-bold">Max Profit: {config.MAX_PROFIT}</div>
                    <div className="bg-red-200 p-1 font-bold">Max Loss: {config.MAX_LOSS}</div>
                    <div className="bg-blue-100 p-1">MTM: {config.MTM}</div>
                    <div className="bg-yellow-100 p-1">ROI: {config.ROI}</div>
                    <div className="bg-gray-100 col-span-2 text-[10px]">SPOT: N: {config.NIFTY_SPOT} | B: {config.BANKNIFTY_SPOT} | S: {config.SENSEX_SPOT}</div>
                    <div className="col-span-2 mt-2 border-t pt-1">
                        <div className="text-[10px] font-bold text-left mb-1">Sell Cap Config</div>
                        <div className="grid grid-cols-3 gap-1">
                            {['NIFTY', 'BANKNIFTY', 'SENSEX'].map(idx => (
                                <div key={idx} className="border p-1">
                                    <div className="font-bold text-[9px]">{idx.charAt(0)}</div>
                                    <Input placeholder="Cap" className="h-5 text-[9px] mb-1"
                                        value={config.SELL_CAP?.[idx]?.[0] || ''}
                                        onChange={(e) => {
                                            const newCap = [...(config.SELL_CAP?.[idx] || [0, 0])];
                                            newCap[0] = e.target.value;
                                            handleInputChange('SELL_CAP', idx, null, newCap);
                                        }}
                                        onKeyDown={handleKeyDown}
                                    />
                                    <Input placeholder="Qty" className="h-5 text-[9px]"
                                        value={config.SELL_CAP?.[idx]?.[1] || ''}
                                        onChange={(e) => {
                                            const newCap = [...(config.SELL_CAP?.[idx] || [0, 0])];
                                            newCap[1] = e.target.value;
                                            handleInputChange('SELL_CAP', idx, null, newCap);
                                        }}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => sendAction('RESET_ALL')}>RESET ALL</Button>
                <Button variant="outline" size="sm" onClick={() => sendAction('EXIT')}>EXIT</Button>
                <Button variant="outline" size="sm" onClick={() => sendAction('EXIT_CE')}>EXIT CE</Button>
                <Button variant="outline" size="sm" onClick={() => sendAction('EXIT_PE')}>EXIT PE</Button>
                <Button variant="outline" size="sm" onClick={() => sendAction('PAUSE')}>PAUSE</Button>
                <Button variant="outline" size="sm" onClick={() => sendAction('CONTINUE')}>CONTINUE</Button>
            </div>

            {/* Trade Grid */}
            <div className="bg-white border shadow overflow-x-auto h-[300px]">
                <table className="w-full text-center border-collapse text-[11px]">
                    <thead className="bg-cyan-300 font-bold sticky top-0 z-10 border-b-2 border-slate-400">
                        <tr>
                            <th className="border p-1">INDEX</th><th className="border p-1">LOT</th><th className="border p-1">Capital</th>
                            <th className="border p-1">TF</th><th className="border p-1">CN</th><th className="border p-1">Ctype</th>
                            <th className="border p-1">BUY / SELL</th><th className="border p-1">LEGS</th><th className="border p-1">Price</th>
                            <th className="border p-1">Immediate Entry</th><th className="border p-1">Update</th>
                            <th className="border p-1">Strategy Name</th><th className="border p-1">NBS</th><th className="border p-1">Start</th>
                            <th className="border p-1">Active PE</th><th className="border p-1">Active CE</th>
                        </tr>
                    </thead>
                    <tbody>
                        {gridData && gridData.length > 0 ? gridData.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50 border-b">
                                <td className="border p-1">{row.INDEX}</td>
                                <td className="border p-1">{row.LOT}</td>
                                <td className="border p-1">{row.Capital}</td>
                                <td className="border p-1">{row.TF}</td>
                                <td className="border p-1">{row.CN}</td>
                                <td className="border p-1">{row.Ctype}</td>
                                <td className={`border p-1 font-bold ${row['BUY / SELL'] === 'Buy' ? 'text-blue-600' : 'text-red-600'}`}>{row['BUY / SELL']}</td>
                                <td className="border p-1">{row.LEGS}</td>
                                <td className="border p-1">{row.Price}</td>
                                <td className="border p-1">{row['Immediate Entry']}</td>
                                <td className="border p-1">{row.Update}</td>
                                <td className="border p-1">{row['Strategy Name']}</td>
                                <td className="border p-1">{row.NBS}</td>
                                <td className="border p-1">{row.Start}</td>
                                <td className="border p-1">{row['Active PE']}</td>
                                <td className="border p-1">{row['Active CE']}</td>
                            </tr>
                        )) : (
                            <tr><td colSpan="16" className="p-4 text-gray-400 italic">No Active Trades</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Sheet2Tab;
